import { zodResolver } from '@hookform/resolvers/zod';
import type { ValidationErrorResponse } from '@wyz/types';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useI18n } from '~/common/context';
import { Validations } from '~/common/helpers';
import { CommonErrorCodesEnumType } from '~/common/types';
import { DistributionPricingPolicyHelpers } from '~/purchase/helpers';
import { distributionExtraBonusDraftSchema } from '~/purchase/schemas';
import {
  IDistributionExtraBonusDraft,
  IDistributionPricingPolicyBrand,
  ISaveDistributionPricingPolicyBrandPayload,
} from '~/purchase/types';

/**
 * One brand at a time is edited, through a react-hook-form form the fields own themselves. The table
 * only reads the values back, so the totals stay in step with the typing.
 *
 * The edition is only closed once the save went through: a refused save keeps the brand open, with the
 * violations the API reported moved back onto the fields that carry them.
 */
export function useExtraBonusEdition(
  onSaveBrand?: (
    payload: ISaveDistributionPricingPolicyBrandPayload,
  ) => Promise<unknown>,
) {
  const { t } = useI18n();
  const [editedBrand, setEditedBrand] = React.useState<string | null>(null);
  const methods = useForm<IDistributionExtraBonusDraft>({
    resolver: zodResolver(distributionExtraBonusDraftSchema),
    defaultValues: DistributionPricingPolicyHelpers.EMPTY_DRAFT,
  });
  const draft = useWatch({
    control: methods.control,
  }) as IDistributionExtraBonusDraft;

  const startEdition = (brand: IDistributionPricingPolicyBrand) => {
    setEditedBrand(brand.brand);
    methods.reset(DistributionPricingPolicyHelpers.toEditionDraft(brand));
  };

  const cancelEdition = () => {
    setEditedBrand(null);
    methods.reset(DistributionPricingPolicyHelpers.EMPTY_DRAFT);
  };

  const submitEdition = async (values: IDistributionExtraBonusDraft) => {
    if (null === editedBrand || !onSaveBrand) {
      return;
    }

    try {
      await onSaveBrand(
        DistributionPricingPolicyHelpers.toSaveBrandPayload(
          editedBrand,
          values,
        ),
      );

      cancelEdition();
    } catch (error) {
      const { errors } =
        (error as ValidationErrorResponse<CommonErrorCodesEnumType>) ?? {};

      Validations.setFormErrors<IDistributionExtraBonusDraft>({
        setError: methods.setError,
        errors: Validations.formatValidationErrors(
          DistributionPricingPolicyHelpers.toExtraBonusFormErrors(
            errors ?? [],
            values,
          ),
          t,
        ),
      });
    }
  };

  return {
    editedBrand,
    methods,
    draft,
    startEdition,
    cancelEdition,
    submitEdition,
  };
}
